import React, { useContext, useMemo, useState } from 'react';
import { AppContext } from 'context/AppContextProvider';
import {
  Box,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  CircularProgress,
  Chip,
  Tooltip,
  Collapse,
  Stack,
  MenuItem,
  TextField,
  Button,
  Link,
  TablePagination,
} from '@mui/material';
import { CheckCircle, RadioButtonUnchecked, KeyboardArrowDown, KeyboardArrowRight, PlayCircleOutline } from '@mui/icons-material';

import {
  useGetVideoProcessingQueueDevicesQuery,
  useGetVideoProcessingQueueQuery,
  useRetryVideosBulkMutation,
  useRetryVideoProcessingMutation,
} from '../../../services/SportVideoQueue/hooks/useQuery';
import {
  IVideoProcessingQueueFilterProps,
  IVideoProcessingQueueItemProps,
} from '../../../services/SportVideoQueue/types';
import { Replay } from '@mui/icons-material';
import { COLORS, ORIGIN_CONFIG, STAGE_CONFIG, STAGE_ORDER } from './_components/Constants';
import { useStableActiveItems } from './hooks/useStableActiveItems';
import QuarantineManagerDialog from './_components/QuarantineManagerDialog';
import { useGetActiveQuarantinesQuery } from '../../../services/SportVideoQueue/quarantine';
import { PauseCircleOutline } from '@mui/icons-material';
import SyncActiveToast from '../../../components/SyncActiveToast';
import { useJobQuery } from '../../../services/Jobs/hooks/useQuery';

// Só é "ativo" (em etapas de processamento) o que ainda não terminou nem falhou.
const ACTIVE_STAGES = new Set([
  'Queued',
  'Downloading',
  'ConvertingCodec',
  'AddingOverlays',
  'GeneratingThumbnail',
  'Uploading',
  'CreatingRecord',
]);

const {
  backgroundColorDarkTheme,
  tableBackgroundColor,
  tableHeaderColor,
  borderColorDarkTheme,
  fontColorDarkTheme,
} = COLORS;

const formatDateTime = (value?: string | null) => (value ? new Date(value).toLocaleString('pt-BR') : '-');

interface StageStepsProps {
  item: IVideoProcessingQueueItemProps;
}

// Só recebe itens ativos (Failed/Completed já são filtrados antes de chegar aqui), então
// currentIndex sempre cai dentro de STAGE_ORDER (Queued..CreatingRecord).
const StageSteps = ({ item }: StageStepsProps) => {
  const currentIndex = STAGE_ORDER.indexOf(item.stage as (typeof STAGE_ORDER)[number]);

  return (
    <Box sx={{ p: 2, backgroundColor: tableHeaderColor, borderRadius: 2 }}>
      <Typography variant="caption" sx={{ color: 'gray', display: 'block', mb: 1.5 }}>
        Etapas realizadas até agora
      </Typography>
      <Stack spacing={1}>
        {STAGE_ORDER.map((stage, index) => {
          const cfg = STAGE_CONFIG[stage];
          const isDone = currentIndex > index;
          const isCurrent = index === currentIndex;
          return (
            <Box key={stage} sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {isDone ? (
                <CheckCircle fontSize="small" sx={{ color: '#66bb6a' }} />
              ) : isCurrent ? (
                <CircularProgress size={14} thickness={6} sx={{ color: '#ffb74d' }} />
              ) : (
                <RadioButtonUnchecked fontSize="small" sx={{ color: 'rgba(255,255,255,0.2)' }} />
              )}
              <Typography
                variant="body2"
                sx={{
                  color: isDone ? '#66bb6a' : isCurrent ? '#ffb74d' : 'rgba(255,255,255,0.35)',
                  fontWeight: isCurrent ? 600 : 400,
                }}
              >
                {cfg.label}
              </Typography>
            </Box>
          );
        })}
      </Stack>
    </Box>
  );
};

const inputSx = {
  '& .MuiInputBase-root': { color: fontColorDarkTheme, backgroundColor: tableHeaderColor },
  '& .MuiInputLabel-root': { color: 'gray' },
  '& .MuiOutlinedInput-notchedOutline': { borderColor: borderColorDarkTheme },
};

const DEFAULT_PAGE_SIZE = 20;

const VideoQueuePage = () => {
  const [deviceId, setDeviceId] = useState<string>('');
  const [date, setDate] = useState<string>('');
  const [startTime, setStartTime] = useState<string>('');
  const [endTime, setEndTime] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [retryOrigin, setRetryOrigin] = useState<string>('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [isQuarantineDialogOpen, setIsQuarantineDialogOpen] = useState(false);
  const [retryJobId, setRetryJobId] = useState<number | null>(null);
  const [isRetryToastOpen, setIsRetryToastOpen] = useState(false);
  const refetchedForRetryJobRef = React.useRef<number | null>(null);
  // TablePagination usa página 0-based; o backend usa PageNumber 1-based.
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE);

  const filter = useMemo(() => {
    // new Date(y, m, d, h, min) usa o horário local do navegador — é assim que interpretamos o que
    // o usuário digitou nos campos de dia/hora. toISOString() converte pra UTC de verdade, então o
    // backend não precisa (e não deve) tentar adivinhar o fuso horário.
    let rangeStart: string | undefined;
    let rangeEnd: string | undefined;

    if (date) {
      const [year, month, day] = date.split('-').map(Number);
      const [startHour, startMinute] = startTime ? startTime.split(':').map(Number) : [0, 0];
      rangeStart = new Date(year, month - 1, day, startHour, startMinute, 0, 0).toISOString();

      if (endTime) {
        const [endHour, endMinute] = endTime.split(':').map(Number);
        rangeEnd = new Date(year, month - 1, day, endHour, endMinute, 0, 0).toISOString();
      } else {
        rangeEnd = new Date(year, month - 1, day + 1, 0, 0, 0, 0).toISOString();
      }
    }

    return {
      deviceId: deviceId ? Number(deviceId) : undefined,
      rangeStart,
      rangeEnd,
      status: (status || undefined) as IVideoProcessingQueueFilterProps['status'],
      retryOrigin: (retryOrigin || undefined) as IVideoProcessingQueueFilterProps['retryOrigin'],
      pageNumber: page + 1,
      pageSize,
    };
  }, [deviceId, date, startTime, endTime, status, retryOrigin, page, pageSize]);

  const { data, isLoading } = useGetVideoProcessingQueueQuery(filter);
  const { data: devices } = useGetVideoProcessingQueueDevicesQuery();
  const { data: quarantines } = useGetActiveQuarantinesQuery();
  const quarantinedDeviceIds = new Set((quarantines ?? []).map((q) => q.deviceId));
  const {
    mutate: retryVideoMutate,
    isLoading: isRetrying,
    variables: retryingId,
  } = useRetryVideoProcessingMutation();
  const { mutateAsync: retryVideosBulk, isLoading: isRetryingMany } = useRetryVideosBulkMutation();
  const { showSnackbar } = useContext(AppContext);
  const { data: retryJob } = useJobQuery(retryJobId);

  const latestActiveItems = (data?.items ?? []).filter((item) => ACTIVE_STAGES.has(item.stage));
  const historyItems = (data?.items ?? []).filter((item) => !ACTIVE_STAGES.has(item.stage));
  const activeItems = useStableActiveItems(latestActiveItems);

  // useStableActiveItems mantém um item "ativo" em cache por alguns segundos depois de sumir do
  // último poll (pra evitar flicker de soluços passageiros de rede). Mas quando o vídeo realmente
  // concluiu/falhou nesse meio-tempo, o mesmo id já aparece em historyItems com o status novo — sem
  // esse filtro, a linha "fantasma" (ainda em cache, mostrando a última etapa antes de terminar)
  // ficava duplicada ao lado da linha real já concluída.
  const historyIds = new Set(historyItems.map((item) => item.id));
  const items = [...activeItems.filter((item) => !historyIds.has(item.id)), ...historyItems];
  const failedItemIds = items.filter((item) => item.status === 'Failed').map((item) => item.id);

  const retryVideo = (id: number) => {
    retryVideoMutate(id, {
      onSuccess: () => showSnackbar('Vídeo reenviado para a fila com sucesso.', 'success'),
      onError: (error: any) => showSnackbar(error?.message || 'Erro ao reenviar vídeo.', 'error'),
    });
  };

  const handleRetryMany = async () => {
    try {
      const { jobId } = await retryVideosBulk(failedItemIds);
      refetchedForRetryJobRef.current = null;
      setRetryJobId(jobId);
      setIsRetryToastOpen(true);
    } catch (error: any) {
      showSnackbar(error?.message || 'Erro ao iniciar retry em lote.', 'error');
    }
  };

  // Quando o job de retry em lote termina, dispara um toast final de sucesso/erro e libera a query
  // da fila pra refletir o resultado (o job já invalida via polling, mas o refetch imediato evita
  // esperar o próximo ciclo de 3s pra ver os primeiros itens mudando de status).
  React.useEffect(() => {
    if (!retryJob) return;
    if (retryJob.status !== 'Completed' && retryJob.status !== 'Failed') return;
    if (refetchedForRetryJobRef.current === retryJobId) return;

    refetchedForRetryJobRef.current = retryJobId;
    showSnackbar(
      retryJob.message || (retryJob.status === 'Completed' ? 'Retry em lote concluído.' : 'Retry em lote falhou.'),
      retryJob.status === 'Completed' ? 'success' : 'error',
    );

    const timer = setTimeout(() => {
      setIsRetryToastOpen(false);
      setRetryJobId(null);
    }, 5000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [retryJob?.status, retryJobId]);

  const toggleExpanded = (id: number) => {
    setExpandedId((prev) => (prev === id ? null : id));
  };

  const clearFilters = () => {
    setDeviceId('');
    setDate('');
    setStartTime('');
    setEndTime('');
    setStatus('');
    setRetryOrigin('');
    setPage(0);
  };

  const handleFilterChange = <T,>(setter: (value: T) => void) => (value: T) => {
    setter(value);
    setPage(0);
  };

  const hasFilters = Boolean(deviceId || date || startTime || endTime || status || retryOrigin);

  return (
    <Box
      sx={{
        p: 4,
        minHeight: 'calc(100vh - 64px)',
        backgroundColor: backgroundColorDarkTheme,
        color: fontColorDarkTheme,
      }}
    >
      <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
        <Box sx={{ mb: 3 }}>
          <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 0.5 }}>
            Fila de Processamento de Vídeo
          </Typography>
          <Typography variant="body2" sx={{ color: 'gray' }}>
            Acompanhamento em tempo real dos vídeos sendo baixados/processados pelo acionamento do botão. Clique numa linha em processamento pra ver as etapas.
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', mb: 3 }}>
          <TextField
            select
            size="small"
            label="Câmera"
            value={deviceId}
            onChange={(e) => handleFilterChange(setDeviceId)(e.target.value)}
            sx={{ minWidth: 220, ...inputSx }}
          >
            <MenuItem value="">Todas</MenuItem>
            {(devices ?? []).map((d) => (
              <MenuItem key={d.deviceId} value={d.deviceId}>
                {d.deviceName || `Dispositivo ${d.deviceId}`}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            type="date"
            size="small"
            label="Dia"
            InputLabelProps={{ shrink: true }}
            value={date}
            onChange={(e) => handleFilterChange(setDate)(e.target.value)}
            sx={{ minWidth: 170, ...inputSx }}
          />

          <TextField
            type="time"
            size="small"
            label="A partir de"
            InputLabelProps={{ shrink: true }}
            value={startTime}
            onChange={(e) => handleFilterChange(setStartTime)(e.target.value)}
            disabled={!date}
            sx={{ minWidth: 150, ...inputSx }}
          />

          <TextField
            type="time"
            size="small"
            label="Até"
            InputLabelProps={{ shrink: true }}
            value={endTime}
            onChange={(e) => handleFilterChange(setEndTime)(e.target.value)}
            disabled={!date}
            sx={{ minWidth: 150, ...inputSx }}
          />

          <TextField
            select
            size="small"
            label="Status"
            value={status}
            onChange={(e) => handleFilterChange(setStatus)(e.target.value)}
            sx={{ minWidth: 160, ...inputSx }}
          >
            <MenuItem value="">Todos</MenuItem>
            <MenuItem value="Processing">Processando</MenuItem>
            <MenuItem value="Completed">Concluído</MenuItem>
            <MenuItem value="Failed">Falhou</MenuItem>
          </TextField>

          <TextField
            select
            size="small"
            label="Origem"
            value={retryOrigin}
            onChange={(e) => handleFilterChange(setRetryOrigin)(e.target.value)}
            sx={{ minWidth: 160, ...inputSx }}
          >
            <MenuItem value="">Todas</MenuItem>
            <MenuItem value="New">Novo</MenuItem>
            <MenuItem value="Automatic">Retry automático</MenuItem>
            <MenuItem value="Manual">Retry manual</MenuItem>
          </TextField>

          {hasFilters && (
            <Button size="small" onClick={clearFilters} sx={{ color: 'gray' }}>
              Limpar filtros
            </Button>
          )}

          {failedItemIds.length > 0 && (
            <Button
              size="small"
              variant="outlined"
              color="warning"
              startIcon={<Replay fontSize="small" />}
              disabled={isRetryingMany}
              onClick={handleRetryMany}
              sx={{ ml: 'auto' }}
            >
              Retentar todos ({failedItemIds.length})
            </Button>
          )}

          <Button
            size="small"
            variant="outlined"
            startIcon={<PauseCircleOutline fontSize="small" />}
            onClick={() => setIsQuarantineDialogOpen(true)}
            sx={{ ml: failedItemIds.length > 0 ? 0 : 'auto' }}
          >
            Quarentena{quarantinedDeviceIds.size > 0 ? ` (${quarantinedDeviceIds.size})` : ''}
          </Button>
        </Box>

        <TableContainer
          component={Paper}
          sx={{
            backgroundColor: tableBackgroundColor,
            borderRadius: 3,
            boxShadow: '0 4px 24px rgba(0,0,0,0.25)',
            border: `1px solid ${borderColorDarkTheme}`,
            overflowX: 'auto',
          }}
        >
          <Table size="small" sx={{ tableLayout: 'fixed', width: '100%', minWidth: 1000, borderCollapse: 'collapse' }}>
            <TableHead>
              <TableRow>
                {[
                  { key: 'expand', label: '', width: '3%' },
                  { key: 'device', label: 'Dispositivo', width: '14%' },
                  { key: 'hall', label: 'Quadra', width: '12%' },
                  { key: 'stage', label: 'Etapa', width: '13%' },
                  { key: 'origin', label: 'Origem', width: '11%' },
                  { key: 'video', label: 'Vídeo', width: '8%' },
                  { key: 'triggeredAt', label: 'Momento do aperto', width: '13%' },
                  { key: 'updatedAt', label: 'Última atualização', width: '13%' },
                  { key: 'actions', label: '', width: '11%' },
                ].map((col) => (
                  <TableCell
                    key={col.key}
                    sx={{ width: col.width, backgroundColor: tableHeaderColor, color: 'gray', fontWeight: 'bold', borderBottom: `1px solid rgba(255,255,255,0.1)` }}
                  >
                    {col.label}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5, borderBottom: 'none' }}>
                    <CircularProgress size={30} sx={{ color: fontColorDarkTheme }} />
                  </TableCell>
                </TableRow>
              ) : items.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={9} align="center" sx={{ py: 5, color: fontColorDarkTheme, borderBottom: 'none' }}>
                    Nenhum vídeo encontrado.
                  </TableCell>
                </TableRow>
              ) : (
                items.map((item: IVideoProcessingQueueItemProps) => {
                  const isActive = ACTIVE_STAGES.has(item.stage);
                  const stageCfg = STAGE_CONFIG[item.stage] ?? { label: item.stage, color: 'default' as any };
                  const isExpanded = expandedId === item.id;
                  return (
                    <React.Fragment key={item.id}>
                      <TableRow
                        hover={isActive}
                        onClick={() => isActive && toggleExpanded(item.id)}
                        sx={{
                          cursor: isActive ? 'pointer' : 'default',
                          '&:nth-of-type(odd)': { backgroundColor: 'rgba(255,255,255,0.02)' },
                          transition: 'background-color 0.15s',
                        }}
                      >
                        <TableCell sx={{ borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {isActive &&
                            (isExpanded ? (
                              <KeyboardArrowDown fontSize="small" sx={{ color: 'gray' }} />
                            ) : (
                              <KeyboardArrowRight fontSize="small" sx={{ color: 'gray' }} />
                            ))}
                        </TableCell>
                        <TableCell sx={{ color: fontColorDarkTheme, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                            {item.deviceName || `Dispositivo ${item.deviceId}`}
                            {quarantinedDeviceIds.has(item.deviceId) && (
                              <Tooltip title="Dispositivo em quarentena — retry automático pausado">
                                <PauseCircleOutline fontSize="small" sx={{ color: '#ef5350' }} />
                              </Tooltip>
                            )}
                          </Box>
                        </TableCell>
                        <TableCell sx={{ color: fontColorDarkTheme, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {item.sportHallName || `Quadra ${item.sportHallId}`}
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          <Tooltip title={item.errorMessage || ''}>
                            <Chip size="small" label={stageCfg.label} color={stageCfg.color} sx={{ borderRadius: 1.5 }} />
                          </Tooltip>
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {(() => {
                            const originCfg = ORIGIN_CONFIG[item.retryOrigin ?? 'New'];
                            return (
                              <Chip size="small" label={originCfg.label} color={originCfg.color} sx={{ borderRadius: 1.5 }} />
                            );
                          })()}
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {item.videoUrl ? (
                            <Link
                              href={item.videoUrl}
                              target="_blank"
                              rel="noopener noreferrer"
                              onClick={(e) => e.stopPropagation()}
                              sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64b5f6' }}
                            >
                              <PlayCircleOutline fontSize="small" /> Ver
                            </Link>
                          ) : (
                            '-'
                          )}
                        </TableCell>
                        <TableCell sx={{ color: fontColorDarkTheme, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {formatDateTime(item.enqueuedAt)}
                        </TableCell>
                        <TableCell sx={{ color: fontColorDarkTheme, borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {formatDateTime(item.updatedAt)}
                        </TableCell>
                        <TableCell sx={{ borderBottom: `1px solid rgba(255,255,255,0.06)` }}>
                          {item.status === 'Failed' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="warning"
                              startIcon={<Replay fontSize="small" />}
                              disabled={isRetrying && retryingId === item.id}
                              onClick={(e) => {
                                e.stopPropagation();
                                retryVideo(item.id);
                              }}
                            >
                              Retentar
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                      {isActive && (
                        <TableRow>
                          <TableCell colSpan={9} sx={{ p: 0, borderBottom: isExpanded ? `1px solid rgba(255,255,255,0.06)` : 'none' }}>
                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                              <Box sx={{ p: 2 }}>
                                <StageSteps item={item} />
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <TablePagination
          component="div"
          count={data?.historyTotalCount ?? 0}
          page={page}
          onPageChange={(_, newPage) => setPage(newPage)}
          rowsPerPage={pageSize}
          onRowsPerPageChange={(e) => {
            setPageSize(Number(e.target.value));
            setPage(0);
          }}
          rowsPerPageOptions={[10, 20, 50, 100]}
          labelRowsPerPage="Itens por página"
          labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
          sx={{ color: fontColorDarkTheme, '& .MuiTablePagination-selectIcon': { color: fontColorDarkTheme } }}
        />
      </Box>

      <QuarantineManagerDialog
        isOpen={isQuarantineDialogOpen}
        onClose={() => setIsQuarantineDialogOpen(false)}
        devices={devices ?? []}
      />

      {retryJob && (
        <SyncActiveToast
          open={isRetryToastOpen}
          personName="Retry de vídeos"
          progress={retryJob.progress}
          statusMessage={retryJob.message ?? undefined}
          success={retryJob.status === 'Completed'}
          successLingerMs={5000}
          operation="update"
          doneMessageOverride={retryJob.message ?? undefined}
          onView={() => {}}
          onClose={() => {
            setIsRetryToastOpen(false);
            setRetryJobId(null);
          }}
        />
      )}
    </Box>
  );
};

export default VideoQueuePage;
